<template>
    <CardFront :data-testid="`entrada-row-${index}`" bgColor="tw:bg-slate-50">
        <div class="tw:flex tw:items-start tw:gap-9 tw:flex-wrap tw:md:flex-nowrap">

            <div v-if="entradaLocal.user_nombre">
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0">{{ $t('perfil.entrada_detalle.titular') }}</p>
                <p class="tw:text-sm tw:font-semibold tw:text-slate-900 tw:m-0 tw:leading-tight">
                    {{ entradaLocal.user_nombre }}  <span>{{ entradaLocal.user_apellidos }}</span>
                </p>
                <p class="tw:text-xs tw:text-slate-500 tw:font-mono tw:m-0">{{ entradaLocal.user_dni }}</p>
            </div>
            <div v-if="entradaLocal.codigo_qr && props.puedeDescargarEntradas">
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0">{{ $t('perfil.entrada_detalle.qr_code') }}</p>
                <div class="tw:flex tw:items-center tw:gap-2">
                    <p class="tw:text-sm tw:font-semibold tw:text-slate-900 tw:m-0 tw:leading-tight">
                        {{ entradaLocal.codigo_qr }}
                    </p>
                    <QrPreview v-if="entradaLocal.codigo_qr" :value="entradaLocal.codigo_qr" />
                </div>
            </div>

            <div v-if="entradaLocal.genero">
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0">{{ $t('perfil.entrada_detalle.genero') }}</p>
                <p class="tw:text-sm tw:font-semibold tw:text-slate-900 tw:m-0 tw:leading-tight">{{ entradaLocal.genero.nombre }}</p>
            </div>

            <div v-if="entradaLocal.asiento || entradaLocal.plaza">
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0">{{ $t('perfil.entrada_detalle.ubicacion') }}</p>
                <p class="tw:text-sm tw:font-semibold tw:text-slate-900 tw:m-0">{{ ubicacionLabel }}</p>
                <p class="tw:text-[10px] tw:text-slate-500 tw:font-bold tw:uppercase tw:m-0">
                    {{ $t('perfil.entrada_detalle.fila_asiento', { fila: filaLabel, asiento: butacaLabel }) }}
                </p>
            </div>

            <div v-if="entradaLocal.date_caducidad">
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0">{{ $t('perfil.entrada_detalle.caducidad') }}</p>
                <p class="tw:text-sm tw:font-semibold tw:text-slate-900 tw:m-0 tw:leading-tight">{{ entradaLocal.date_caducidad }}</p>
            </div>

            <div>
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0 tw:mb-1">{{ $t('perfil.entrada_detalle.enviado') }}</p>
                <div class="tw:flex tw:items-center tw:gap-1">
                    <BadgeFront :label="estaEnviada ? $t('perfil.si_enviado') : $t('perfil.no_enviado')" :variant="enviadoVariant" custom-class="tw:w-14 tw:justify-center" />
                    <PopoverFront v-if="estaEnviada" position="top">
                        <template #trigger>
                            <i class="material-icons tw:text-sm tw:text-slate-400 tw:hover:text-slate-600 tw:cursor-pointer">info</i>
                        </template>
                        <div class="tw:space-y-1 tw:pr-4">
                            <p v-if="entradaLocal.enviado.email" class="tw:m-0 tw:flex tw:items-center tw:gap-1">
                                <i class="material-icons tw:text-xs tw:text-slate-400">email</i>
                                <span>{{ entradaLocal.enviado.email }}</span>
                            </p>
                            <p v-if="entradaLocal.enviado.telefono" class="tw:m-0 tw:flex tw:items-center tw:gap-1">
                                <i class="material-icons tw:text-xs tw:text-slate-400">phone</i>
                                <span>{{ entradaLocal.enviado.telefono }}</span>
                            </p>
                            <p v-if="entradaLocal.enviado.fecha" class="tw:m-0 tw:flex tw:items-center tw:gap-1">
                                <i class="material-icons tw:text-xs tw:text-slate-400">calendar_today</i>
                                <span>{{ entradaLocal.enviado.fecha }}</span>
                            </p>
                        </div>
                    </PopoverFront>
                </div>
            </div>

            <div>
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0 tw:mb-1">{{ $t('perfil.entrada_detalle.anulada') }}</p>
                <BadgeFront :label="estaAnulada ? $t('perfil.si_enviado') : $t('perfil.no_enviado')" :variant="anuladaVariant" custom-class="tw:w-14 tw:justify-center" />
            </div>

            <div v-if="teatro?.seguro_entradas_activo && !estaAnulada">
                <p class="tw:text-[10px] tw:font-bold tw:text-slate-400 tw:uppercase tw:m-0 tw:mb-1">{{ $t('perfil.entrada_detalle.seguro') }}</p>
                <BadgeFront
                    :label="entradaLocal.seguro_entradas ? $t('perfil.si_enviado') : $t('perfil.no_enviado')"
                    :variant="entradaLocal.seguro_entradas ? 'info' : 'danger'"
                    :icon="'shield'"
                    :dot="false"
                    custom-class="tw:w-14 tw:justify-center"
                />
            </div>

        </div>

        <template v-if="!estaAnulada" #footer>
            <div class="tw:flex tw:gap-2 tw:pt-2 tw:border-t tw:border-slate-100 tw:flex-wrap">
                <Button v-if="props.puedeDescargarEntradas" :data-testid="`entrada-row-btn-descargar-${index}`" variant="default-tiny" as="a" :href="entradaLocal.pdf_url" target="_blank" rel="noopener">
                    <i class="material-icons tw:text-sm">download</i> {{ $t('perfil.entrada_detalle.descargar') }}
                </Button>
                <Button :data-testid="`entrada-row-btn-enviar-${index}`" variant="default-tiny" @click="showSendModal = true">
                    <i class="material-icons tw:text-sm">send</i> {{ $t('perfil.entrada_detalle.enviar') }}
                </Button>
                <Button v-if="entradaLocal.wallet_pass && props.puedeDescargarEntradas" variant="default-tiny" as="a" :href="entradaLocal.wallet_pass" target="_blank" rel="noopener">
                    <i class="material-icons tw:text-sm">account_balance_wallet</i> Wallet
                </Button>
                <Button
                    v-if="pedido.evento?.solicitar_datos && entradaLocal.puede_cambiar_datos && !entradaLocal.deleted_at && sinCosteDeCambio"
                    :data-testid="`entrada-row-btn-cambiar-datos-${index}`"
                    variant="default-tiny"
                    @click="showEditModal = true"
                >
                    <i class="material-icons tw:text-sm">edit</i> {{ $t('perfil.cambiar_datos') }}
                    <PopoverFront v-if="entradaLocal.fecha_limite_cambiar_datos" position="top">
                        <template #trigger>
                            <i class="material-icons tw:text-xs tw:ml-1 tw:opacity-70">info</i>
                        </template>
                        <p class="tw:m-0 tw:text-xs">
                            {{ $t('perfil.limite_cambiar_datos', { fecha_fin_evento: entradaLocal.fecha_limite_cambiar_datos.nombre }) }}
                        </p>
                    </PopoverFront>
                </Button>
            </div>
            <SendTicketModalFront
                :show="showSendModal"
                :entrada="entradaLocal"
                :pedido="pedido"
                @close="showSendModal = false"
                @success="showSendModal = false"
            />
            <EditTicketModal
                :show="showEditModal"
                :entrada="entradaLocal"
                :pedido-id="pedido.id"
                :todas-entradas="todasEntradas"
                @close="showEditModal = false"
                @success="onEditSuccess"
            />
        </template>
    </CardFront>
</template>

<script setup>
    import { ref, computed } from 'vue';
    import store from '@/store';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import BadgeFront from '@/modules/front/ui/BadgeFront.vue';
    import PopoverFront from '@/modules/front/ui/PopoverFront.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import SendTicketModalFront from './SendTicketModalFront.vue';
    import EditTicketModal from './EditTicketModal.vue';
    import QrPreview from '@/modules/ui/QrPreview.vue';

    const props = defineProps({
        entrada: { type: Object, required: true },
        pedido:  { type: Object, required: true },
        index:   { type: Number, required: true },
        todasEntradas: { type: Array, default: () => [] },
        puedeDescargarEntradas: { type: Boolean, default: true },
    });

    const entradaLocal = ref({ ...props.entrada });

    const onEditSuccess = ({ nombre, apellidos, dni }) => {
        entradaLocal.value.user_nombre    = nombre;
        entradaLocal.value.user_apellidos = apellidos;
        entradaLocal.value.user_dni       = dni;
        showEditModal.value = false;
    };

    const teatro = computed(() => store.getters.getTeatro);
    const showSendModal  = ref(false);
    const showEditModal  = ref(false);
    const estaEnviada   = computed(() => !!(entradaLocal.value.enviado && entradaLocal.value.enviado.email));
    const estaAnulada   = computed(() => !!entradaLocal.value.deleted_at);
    const sinCosteDeCambio = computed(() => Number(props.pedido?.evento?.coste_cambio_nombre) === 0);
    const enviadoVariant = computed(() => estaEnviada.value ? 'success' : 'default');
    const anuladaVariant = computed(() => estaAnulada.value ? 'danger' : 'default');

    const asientoSrc = computed(() => entradaLocal.value.asiento ?? entradaLocal.value.plaza);
    const ubicacionLabel = computed(() => asientoSrc.value?.region?.nombre ?? asientoSrc.value?.zona ?? '—');
    const filaLabel = computed(() => asientoSrc.value?.row ?? asientoSrc.value?.fila ?? '—');
    const butacaLabel = computed(() => asientoSrc.value?.seat ?? asientoSrc.value?.butaca ?? asientoSrc.value?.asiento ?? '—');
</script>
